import React, { useCallback, useEffect, useRef, useState } from "react";

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  disabled?: boolean;
}

export function Tooltip({
  children,
  content,
  position = "top",
  delay = 300,
  disabled = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState(position);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current || !tooltipRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const padding = 8;

    let top = 0;
    let left = 0;
    let preferredPosition = position;

    const spaceAbove = container.top;
    const spaceBelow = window.innerHeight - container.bottom;
    const spaceLeft = container.left;
    const spaceRight = window.innerWidth - container.right;

    if (position === "top" && spaceAbove < tooltip.height + padding) {
      if (spaceBelow > spaceAbove) {
        preferredPosition = "bottom";
      } else if (spaceLeft > spaceRight) {
        preferredPosition = "left";
      } else {
        preferredPosition = "right";
      }
    }

    switch (preferredPosition) {
      case "top":
        top = container.top - tooltip.height - padding;
        left = container.left + (container.width - tooltip.width) / 2;
        break;
      case "bottom":
        top = container.bottom + padding;
        left = container.left + (container.width - tooltip.width) / 2;
        break;
      case "left":
        top = container.top + (container.height - tooltip.height) / 2;
        left = container.left - tooltip.width - padding;
        break;
      case "right":
        top = container.top + (container.height - tooltip.height) / 2;
        left = container.right + padding;
        break;
    }

    setActualPosition(preferredPosition);
    setTooltipPosition({ top, left });
  }, [position]);

  const showTooltip = useCallback(() => {
    if (disabled || !content) return;
    timeoutRef.current = window.setTimeout(() => {
      calculatePosition();
      setIsVisible(true);
    }, delay);
  }, [disabled, content, delay, calculatePosition]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  }, []);

  const handleMouseEnter = () => {
    if (window.matchMedia("(hover: hover)").matches) {
      showTooltip();
    }
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  const handleFocus = () => {
    showTooltip();
  };

  const handleBlur = () => {
    hideTooltip();
  };

  const handleTouchStart = () => {
    if (window.matchMedia("(hover: none)").matches) {
      showTooltip();
    }
  };

  const handleTouchEnd = () => {
    hideTooltip();
  };

  const handleTouchMove = () => {
    hideTooltip();
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
    }
  }, [isVisible, calculatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-t-slate-900",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-b-slate-900",
    left: "left-full top-1/2 -translate-y-1/2 border-l-slate-900",
    right: "right-full top-1/2 -translate-y-1/2 border-r-slate-900",
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      {children}
      {isVisible && content && (
        <>
          <div
            ref={tooltipRef}
            className={`fixed z-50 ${positionClasses[actualPosition]} pointer-events-none`}
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
          >
            <div className="bg-slate-900 text-slate-200 text-sm px-3 py-2 rounded-lg shadow-xl max-w-xs animate-fade-in whitespace-pre-wrap">
              {content}
            </div>
            <div
              className={`absolute w-0 h-0 border-4 border-transparent ${arrowClasses[actualPosition]}`}
            />
          </div>
        </>
      )}
    </div>
  );
}

interface RoomTooltipProps {
  roomName: string;
  roomDescription?: string;
  children: React.ReactNode;
}

export function RoomTooltip({
  roomName,
  roomDescription,
  children,
}: RoomTooltipProps) {
  const content = roomDescription
    ? `${roomName}\n\n${roomDescription}`
    : roomName;

  return (
    <Tooltip content={content} position="top" delay={200}>
      {children}
    </Tooltip>
  );
}
